// Import React tools used by this page
import React, { useContext, useEffect, useMemo, useState } from "react";

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import strich api calls
import {
    getRateCard,
    getSubscriptionPackages,
    getSubscriptionLevels,
} from "api/APIStrichCalls";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";
import usePodPricingData from "./hooks/usePodPricingData";

// Import page components
import { Grid, GridItem } from "global/components/Grid";
import PlanSelector from "./components/PlanSelector";
import PricingToolbar from "./components/PricingToolbar";
import PricingInsightsBar from "./components/PricingInsightsBar";
import ProductPricingCard from "./components/ProductPricingCard";
import { FormatCurrency } from "global/utilities/ConvertCurrency";
import SubscriptionOptionsPopup from "pages/account/components/SubscriptionOptionsPopUp";

// Import page css
import "./PlansPage.css";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import LoadingScreen from "global/components/loading_ui/LoadingScreen";




// This calculates an average value from an array of numbers
const getAverage = (values = []) => {
    const safeValues = values.filter((value) => Number.isFinite(Number(value)));

    if (!safeValues.length) {
        return 0;
    }

    const total = safeValues.reduce((runningTotal, value) => runningTotal + Number(value), 0);
    return total / safeValues.length;
};



// This is the main POD pricing page component
const PlansPage = () => {

    // Set title inside the page
    usePageTitle("Plans & Pricing");

    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Get user acccount data
    const { userData, userDataErrorMessage, refreshUserData } = useContext(UserContext);
  
    // Store the rate card
    const [rateCard, setRateCard] = useState(null);

    // Store the subscription packages
    const [subscriptionPackages, setSubscriptionPackages] = useState(null);

    // Store the subscription levels
    const [subscriptionLevels, setSubscriptionLevels] = useState(null);

    // Store the current search value for filtering products
    const [searchValue, setSearchValue] = useState("");

    // Store the current selected sort mode
    const [selectedSort, setSelectedSort] = useState("title-asc");

    // Store whether prices should show including tax
    const [showIncludingTax, setShowIncludingTax] = useState(false);

    // Store whether POD fulfilment prices should show including tax
    const [showFulfilmentPricesIncludingTax, setShowFulfilmentPricesIncludingTax] = useState(false);

    // Store the active products tab
    const [activeProductsTab] = useState("search");

    // Store the currently selected product for focus styling
    const [breakEvenProduct, setBreakEvenProduct] = useState(null);

    // Store the currently open product card sku
    const [openProductSku, setOpenProductSku] = useState(null);

    // Store whether the plan selector is collapsed
    const [plansCollapsed, setPlansCollapsed] = useState(false);

    // Store whether the product pricing list is collapsed
    const [productsCollapsed, setProductsCollapsed] = useState(false);

    // Store whether a parent product is expanded to show its children
    const [parentProductIsExpanded, setParentProductIsExpanded] = useState(null);

    // Track whether the initial product expansion has been applied
    const [initialProductExpansionApplied, setInitialProductExpansionApplied] = useState(false);

    // This controls whether the popup is open
    const [isUpgradePopupOpen, setIsUpgradePopupOpen] = useState(false);

    // Loading state when requesting an upgrade
    const [loadingUpgradeRequest, setLoadingUpgradeRequest] = useState(false);

    // This stores the plan and package the user clicked before opening the upgrade popup
    const [defaultPlanToSelect, setDefaultPlanToSelect] = useState(null);

    // This opens the upgrade popup with the clicked package pre-selected
    const handleOpenUpgrade = (plan, packageKey) => {

        // Get the selected package from the clicked plan
        const selectedPackage = plan?.packages?.[packageKey];

        // Store the selected plan and package code for the popup
        setDefaultPlanToSelect({
            ...plan,
            selectedPackageCode: selectedPackage?.code || "",
        });

        // Open the upgrade popup
        setIsUpgradePopupOpen(true);

    };



    // Fetch all page data
    const fetchPodPricePageData = async () => {

        // Return early until the account currency is available
        if (!userData?.currency) { return; }

        // Get the rate card
        const rateCardResponse = await getRateCard(token);

        if (rateCardResponse.success) {

              // Update state
            setRateCard(rateCardResponse);

        } 

        // Get subscription packages in the account currency
        const subscriptionPackagesResponse = await getSubscriptionPackages(token, null, userData?.currency);

        if (subscriptionPackagesResponse.success) {

            // Update state
            setSubscriptionPackages(subscriptionPackagesResponse);

        } 

        // Get the subscription levels
        const subscriptionLevelsResponse = await getSubscriptionLevels(token);

        if (subscriptionLevelsResponse.success) {

            // Update state
            setSubscriptionLevels(subscriptionLevelsResponse);


        } 

    };


    // Run when the token and account currency are available
    useEffect(() => {

        // Fetch the pricing page data
        fetchPodPricePageData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, userData?.currency]);



    // Get normalized data and pricing helpers from the custom hook
    const {
        normalizedPlans,
        normalizedProducts,
        findPriceRowByLevel,
        getMonthlyEquivalent,
        getPackageVisiblePrice,
        getRateVisiblePrice,
        getProductTotalAtLevel,
    } = usePodPricingData({
        rateCard,
        subscriptionPackages,
        subscriptionLevels,
    });

    

    const currentAccountSubscription = subscriptionLevels ? subscriptionLevels?.results?.find((level) => level.code === userData?.active_subscription?.subscription_level)?.level || null : null;

    // Find the default selected plan level from the first sorted plan
    const defaultSelectedLevel = currentAccountSubscription || normalizedPlans?.[0]?.level || null;

    // Store the currently selected plan level
    const [selectedLevel, setSelectedLevel] = useState(defaultSelectedLevel);

    // Find the selected plan object from the normalized plans list
    const selectedPlan = normalizedPlans.find((plan) => Number(plan?.level) === Number(selectedLevel)) || null;

    // Treat the lowest sorted plan as the baseline plan
    const registeredPlan = normalizedPlans?.[0] || null;

    // This is the user's current subscription plan level for comparison
    const currentSubscriptionLevel = subscriptionPackages?.results?.find((subscriptiionPackage) => subscriptiionPackage.code === userData?.active_subscription?.subscription_package)?.level || 3;

    // This finds the user's current subscription plan object
    const currentPlan = normalizedPlans.find((plan) => Number(plan?.level) === Number(currentSubscriptionLevel)) || null;

    // Store selected subscription levels to compare in the rate card
    const [comparisonLevels, setComparisonLevels] = useState([]);



    // This sets the selected plan once plan data has loaded
    useEffect(() => {

        if (!selectedLevel && normalizedPlans.length) {

            setSelectedLevel(defaultSelectedLevel);

        }

    }, [normalizedPlans, selectedLevel, defaultSelectedLevel]);



    // This keeps the user's current subscription level selected in the comparison list
    useEffect(() => {

        // Stop until plans and current subscription level are available
        if (!normalizedPlans.length || !currentSubscriptionLevel) {

            return;

        }

        // Update comparison levels safely
        setComparisonLevels((previousLevels) => {

            // Build valid level list
            const validLevels = normalizedPlans.map((plan) => Number(plan?.level));

            // Read current level
            const currentLevel = Number(currentSubscriptionLevel);

            // Find the next highest subscription level, if there is one
            const nextHighestLevel = validLevels.find((level) => level > currentLevel) || null;

            // Keep previous valid levels only
            const nextLevels = Array.isArray(previousLevels)
                ? previousLevels.map((level) => Number(level)).filter((level) => validLevels.includes(level))
                : [];

            // If nothing has been chosen yet, default to current plus the next highest level
            if (nextLevels.length === 0) {

                nextLevels.push(currentLevel);

                if (nextHighestLevel) {

                    nextLevels.push(nextHighestLevel);

                }

                return nextLevels.sort((a, b) => a - b);

            }

            // Always include the current level
            if (!nextLevels.includes(currentLevel)) {

                nextLevels.push(currentLevel);

            }

            // Return sorted levels
            return nextLevels.sort((a, b) => a - b);

        });

    }, [normalizedPlans, currentSubscriptionLevel]);



    // This toggles a comparison level in the rate card
    const handleToggleComparisonLevel = (level) => {

        // Read selected level
        const safeLevel = Number(level);

        // Current subscription level cannot be deselected
        if (safeLevel === Number(currentSubscriptionLevel)) {

            return;

        }

        // Toggle the selected level
        setComparisonLevels((previousLevels) => {

            // Build previous level list
            const nextLevels = Array.isArray(previousLevels) ? previousLevels.map((item) => Number(item)) : [];

            // Remove when already selected
            if (nextLevels.includes(safeLevel)) {

                return nextLevels.filter((item) => item !== safeLevel);

            }

            // Add when not selected
            return [...nextLevels, safeLevel].sort((a, b) => a - b);

        });

    };



    // Find the plan directly below the selected plan for upgrade comparisons
    const previousPlan = useMemo(() => {

        const selectedIndex = normalizedPlans.findIndex((plan) => Number(plan?.level) === Number(selectedLevel));

        return selectedIndex > 0 ? normalizedPlans[selectedIndex - 1] : null;

    }, [normalizedPlans, selectedLevel]);



    // Build the filtered and sorted visible products list
    const visibleProducts = useMemo(() => {

        const query = searchValue.trim().toLowerCase();

        // Filter products using the search value
        const filteredProducts = normalizedProducts.filter((product) => {

            // Convert the parent title into searchable text
            const productTitle = String(product?.title || "").toLowerCase();

            // Convert the parent SKU into searchable text
            const productSku = String(product?.sku || "").toLowerCase();



            // Return every product when there is no search query
            if (!query) {

                return true;

            }



            // Check whether the parent itself matches the search
            const parentMatches =
                productTitle.includes(query) ||
                productSku.includes(query);



            // Check whether any child product matches the search
            const childMatches = (product?.children || []).some((childProduct) => {

                // Convert the child title into searchable text
                const childTitle = String(childProduct?.title || "").toLowerCase();

                // Convert the child SKU into searchable text
                const childSku = String(childProduct?.sku || "").toLowerCase();

                // Return whether this child matches
                return (
                    childTitle.includes(query) ||
                    childSku.includes(query)
                );

            });



            // Show the parent group when either the parent or one of its children matches
            return parentMatches || childMatches;

        });


        return [...filteredProducts].sort((a, b) => {

            const aBaseRow = findPriceRowByLevel(a?.base_price || [], selectedLevel);
            const bBaseRow = findPriceRowByLevel(b?.base_price || [], selectedLevel);
            const aBase = getRateVisiblePrice(aBaseRow, showIncludingTax);
            const bBase = getRateVisiblePrice(bBaseRow, showIncludingTax);
            const aAllAreas = (a?.print_areas || []).map((item) => item?.print_area);
            const bAllAreas = (b?.print_areas || []).map((item) => item?.print_area);

            const aRegisteredTotal = getProductTotalAtLevel({
                product: a,
                level: registeredPlan?.level,
                selectedPrintAreas: aAllAreas,
                showIncludingTax,
            });

            const aSelectedTotal = getProductTotalAtLevel({
                product: a,
                level: selectedLevel,
                selectedPrintAreas: aAllAreas,
                showIncludingTax,
            });

            const bRegisteredTotal = getProductTotalAtLevel({
                product: b,
                level: registeredPlan?.level,
                selectedPrintAreas: bAllAreas,
                showIncludingTax,
            });

            const bSelectedTotal = getProductTotalAtLevel({
                product: b,
                level: selectedLevel,
                selectedPrintAreas: bAllAreas,
                showIncludingTax,
            });

            const aSavings = aRegisteredTotal - aSelectedTotal;
            const bSavings = bRegisteredTotal - bSelectedTotal;

            if (selectedSort === "title-asc") {
                return String(a?.title || "").localeCompare(String(b?.title || ""));
            }

            if (selectedSort === "title-desc") {
                return String(b?.title || "").localeCompare(String(a?.title || ""));
            }

            if (selectedSort === "base-low-high") {
                return aBase - bBase;
            }

            if (selectedSort === "base-high-low") {
                return bBase - aBase;
            }

            if (selectedSort === "savings-high-low") {
                return bSavings - aSavings;
            }

            if (selectedSort === "print-areas-high-low") {
                return (b?.print_areas?.length || 0) - (a?.print_areas?.length || 0);
            }

            return 0;

        });

    }, [
        searchValue,
        normalizedProducts,
        selectedSort,
        selectedLevel,
        showIncludingTax,
        registeredPlan,
        findPriceRowByLevel,
        getRateVisiblePrice,
        getProductTotalAtLevel,
    ]);



    // Automatically expand the first product when the rate card first loads
    useEffect(() => {

        // Only apply the initial expansion once
        if (initialProductExpansionApplied) { return; }

        // Wait until the rate card contains products
        if (!visibleProducts.length) { return; }

        // Get the first visible product
        const firstProduct = visibleProducts[0];

        // Get its first child when this is a parent product
        const firstChild = firstProduct?.children?.[0];

        // Expand the parent and its first child when children exist
        if (firstChild) {

            setParentProductIsExpanded(firstProduct.sku);
            setOpenProductSku(firstChild.sku);

        } else {

            // Otherwise expand the product itself
            setOpenProductSku(firstProduct.sku);

        }

        // Do not interfere with the user's expand/collapse choices after initial load
        setInitialProductExpansionApplied(true);

    }, [initialProductExpansionApplied, visibleProducts]);



    // Calculate the summary metrics for the currently visible products
    const summaryMetrics = useMemo(() => {

        const basePrices = visibleProducts.map((product) => {

            const matchedBaseRow = findPriceRowByLevel(product?.base_price || [], selectedLevel);

            return getRateVisiblePrice(matchedBaseRow, showIncludingTax);

        });

        const allSavings = visibleProducts.map((product) => {

            const allAreas = (product?.print_areas || []).map((printArea) => printArea?.print_area);

            const baselineTotal = getProductTotalAtLevel({
                product,
                level: registeredPlan?.level,
                selectedPrintAreas: allAreas,
                showIncludingTax,
            });

            const selectedTotal = getProductTotalAtLevel({
                product,
                level: selectedLevel,
                selectedPrintAreas: allAreas,
                showIncludingTax,
            });

            return baselineTotal - selectedTotal;

        });

        return {

            lowestBasePrice: basePrices.length ? Math.min(...basePrices) : 0,
            averageBasePrice: getAverage(basePrices),
            maxSavingsVsRegistered: allSavings.length ? Math.max(...allSavings) : 0,

        };

    }, [
        visibleProducts,
        selectedLevel,
        showIncludingTax,
        registeredPlan,
        findPriceRowByLevel,
        getRateVisiblePrice,
        getProductTotalAtLevel,
    ]);



    // If the account data isn't returned and there's an error display it to the user
    if (!userData?.currency && userDataErrorMessage) {

        return (

           <Grid>
                   
                <GridItem>

                    <div className="dashboard-status-panel dashboard-status-panel--error">

                        <h3>Plans unavailable</h3>
                        <p>{userDataErrorMessage}</p>

                    </div>

                </GridItem>

            </Grid>

        );

    }


    // If there are no plans at all show loading spinner
    if (!normalizedPlans.length) {

        return (

            <div className="pod-pricing-page">

                <LoadingSpinner />

            </div>

        );

    }



    // If plans exist but selected plan has not been set yet render nothing or a loading state
    if (!selectedPlan) {

        return null;

    }



    // Render the full page
    return (

        <div className="pod-pricing-page">

            {/* 
            <PricingHero
                currentPlan={currentPlan}
                selectedPlan={selectedPlan}
                selectedPlanDisplayPrice={selectedPlanDisplayPrice}
                selectedPlanYearlyEquivalent={selectedPlanYearlyEquivalent}
                recommendationLabel={recommendationLabel}
                onRecommendationClick={() => {}}
                showIncludingTax={showIncludingTax}
                setIsUpgradePopupOpen={setIsUpgradePopupOpen}
            />
            */}

            <PlanSelector
                plans={normalizedPlans}
                currentPlan={currentPlan}
                selectedLevel={selectedLevel}
                onSelectPlan={setSelectedLevel}
                showIncludingTax={showIncludingTax}
                onToggleTaxMode={setShowIncludingTax}
                plansCollapsed={plansCollapsed}
                onTogglePlansCollapsed={() => setPlansCollapsed((prev) => !prev)}
                formatMoney={(value) => FormatCurrency(userData?.currency, value)}
                getPackageVisiblePrice={getPackageVisiblePrice}
                getMonthlyEquivalent={getMonthlyEquivalent}
                PricingInsightsBar={PricingInsightsBar}
                selectedPlan={selectedPlan}
                summaryMetrics={summaryMetrics}
                onOpenUpgrade={handleOpenUpgrade}
                activeSubscription={userData?.active_subscription || {}}
            />


            <section className="pod-pricing-page__products-section">

                <div className="pod-pricing-page__panel-group">

                    <div
                        className="pod-pricing-page__section-heading-row pod-pricing-page__section-heading-row--clickable"
                        role="button"
                        tabIndex={0}
                        onClick={() => setProductsCollapsed((prev) => !prev)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setProductsCollapsed((prev) => !prev);
                            }
                        }}
                    >

                        <div className="pod-pricing-page__section-heading-copy">

                            <h2 className="pod-pricing-page__section-title">POD Fulfilment Rate Card</h2>

                            <p className="pod-pricing-page__section-text">
                                Review print on demand base product costs, print area pricing, savings versus your current plan, and open product-level detail tables.
                            </p>

                        </div>

                        <div className="pod-pricing-page__section-collapse-container">

                            {/* This is the collapse toggle */}
                            <button
                                type="button"
                                className="pod-pricing-page__icon-button"
                                title={productsCollapsed ? "Expand rate card" : "Collapse rate card"}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setProductsCollapsed((prev) => !prev);
                                }}
                            >

                                {productsCollapsed ? "⮟" : "⮝"}

                            </button>

                        </div>

                    </div>

                    {productsCollapsed ? 
            
                        <></>
        
                        :

                        <>
                        
                            <PricingToolbar
                                searchValue={searchValue}
                                onSearchChange={setSearchValue}
                                comparisonPlans={normalizedPlans}
                                comparisonLevels={comparisonLevels}
                                currentSubscriptionLevel={currentSubscriptionLevel}
                                onToggleComparisonLevel={handleToggleComparisonLevel}
                                selectedSort={selectedSort}
                                onSortChange={setSelectedSort}
                                showIncludingTax={showFulfilmentPricesIncludingTax}
                                onToggleTaxMode={setShowFulfilmentPricesIncludingTax}
                            />

                            {activeProductsTab === "search" ? (

                                <div className="pod-pricing-page__products-list">

                                    {visibleProducts.map((product) => {

                                        const hasChildren = product.children && product.children?.length > 0;
                                      
                                        // If the product has children then display and expandable parent div
                                        if (hasChildren) {

                                            return (

                                                <div
                                                    key={product.sku} 
                                                    className={`pod-pricing-page__parent-card`}
                                                    onClick={() => parentProductIsExpanded === product.sku ? 
                                                        setParentProductIsExpanded(null)
                                                        : setParentProductIsExpanded(product.sku)
                                                    }
                                                >

                                                    <div className="pod-pricing-page__parent-header">

                                                        <div>

                                                            <div className="pod-pricing-page__product-title-row">

                                                                <span className="pod-pricing-page__parent-expand-icon" title={parentProductIsExpanded === product.sku ? "Collapse children products" : "Expand children products"}>
                                                                    {parentProductIsExpanded === product.sku ? "⮝" : "⮟"}
                                                                </span>

                                                                <h3>{product.title}</h3>

                                                            </div>

                                                            <span className="pod-pricing-page__product-sku">{product.sku}</span> 
                                                            <br/>
                                                            <span>Children Products: {product.children.length} </span>

                                                        </div>

                                                        <div>

                                                        </div>

                                                    </div>

                                                    {parentProductIsExpanded === product.sku &&

                                                        product.children.map((child) => (

                                                            <ProductPricingCard
                                                                key={child?.sku}
                                                                product={child}
                                                                plans={normalizedPlans}
                                                                selectedLevel={selectedLevel}
                                                                registeredLevel={registeredPlan?.level}
                                                                currentSubscriptionLevel={currentSubscriptionLevel}
                                                                currentPlan={currentPlan}
                                                                comparisonLevels={comparisonLevels}
                                                                selectedPlan={selectedPlan}
                                                                previousPlan={previousPlan}
                                                                showIncludingTax={showFulfilmentPricesIncludingTax}
                                                                selectedForBreakEven={breakEvenProduct?.sku === child?.sku}
                                                                onSelectForBreakEven={setBreakEvenProduct}
                                                                isOpen={openProductSku === child?.sku}
                                                                onToggleOpen={(productSku) => {

                                                                    setOpenProductSku((prevOpenProductSku) => {

                                                                        return prevOpenProductSku === productSku ? null : productSku;

                                                                    });

                                                                }}
                                                                formatMoney={(value) => FormatCurrency(userData?.currency, value)}
                                                                findPriceRowByLevel={findPriceRowByLevel}
                                                                getRateVisiblePrice={getRateVisiblePrice}
                                                                getProductTotalAtLevel={getProductTotalAtLevel}
                                                                getPackageVisiblePrice={getPackageVisiblePrice}
                                                                getMonthlyEquivalent={getMonthlyEquivalent}
                                                            />

                                                        ))

                                                    }

                                                </div>

                                            )

                                        } 

                                        return (


                                                <ProductPricingCard
                                                    key={product?.sku}
                                                    product={product}
                                                    plans={normalizedPlans}
                                                    selectedLevel={selectedLevel}
                                                    registeredLevel={registeredPlan?.level}
                                                    currentSubscriptionLevel={currentSubscriptionLevel}
                                                    currentPlan={currentPlan}
                                                    comparisonLevels={comparisonLevels}
                                                    selectedPlan={selectedPlan}
                                                    previousPlan={previousPlan}
                                                    showIncludingTax={showFulfilmentPricesIncludingTax}
                                                    selectedForBreakEven={breakEvenProduct?.sku === product?.sku}
                                                    onSelectForBreakEven={setBreakEvenProduct}
                                                    isOpen={openProductSku === product?.sku}
                                                    onToggleOpen={(productSku) => {

                                                        setOpenProductSku((prevOpenProductSku) => {

                                                            return prevOpenProductSku === productSku ? null : productSku;

                                                        });

                                                    }}
                                                    formatMoney={(value) => FormatCurrency(userData?.currency, value)}
                                                    findPriceRowByLevel={findPriceRowByLevel}
                                                    getRateVisiblePrice={getRateVisiblePrice}
                                                    getProductTotalAtLevel={getProductTotalAtLevel}
                                                    getPackageVisiblePrice={getPackageVisiblePrice}
                                                    getMonthlyEquivalent={getMonthlyEquivalent}
                                                />


                                        )

                                    })}

                                </div>
                                
                            ) : (

                                <div className="pod-pricing-page__all-products-placeholder">

                                    <h3 className="pod-pricing-page__panel-title">All Products</h3>

                                    <p className="pod-pricing-page__section-text">

                                        Placeholder for the future all-products table view.

                                    </p>

                                </div>

                            )}

                        </>

                    }
                    
                </div>

            </section>

            {isUpgradePopupOpen &&

                <SubscriptionOptionsPopup
                    activeSubscription={userData?.active_subscription || {}}
                    onClose={() => setIsUpgradePopupOpen(false)}
                    subscriptionLevels={subscriptionLevels.results || []}
                    canUpgrade={userData?.stripe_billing_last4 ? true : false}
                    currentSubscriptionLevelIsFree={userData?.active_subscription.subscription_level === "free"}
                    userData={userData}
                    defaultPlanToSelect={defaultPlanToSelect || selectedPlan}
                    setIsUpgradePopupOpen={setIsUpgradePopupOpen}
                    setLoading={setLoadingUpgradeRequest}
                    refreshUserData={refreshUserData}
                />

            }

            {loadingUpgradeRequest &&
            
                <LoadingScreen loadingMessage="Upgrading..." />
            
            }


        </div>

    );
    
};

export default PlansPage;